import { useMutation, useQuery, useQueryClient, type QueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useUnit } from 'effector-react';
import { isHTTPError } from 'ky';
import { queryKeys } from '~/shared/api/queryKeys';
import { xHeaders } from '~/shared/constants';
import {
    type PayoutTraderOrderV1AcceptOrderRequest,
    type PayoutTraderOrderV1DeclineOrderRequest,
    type PayoutTraderOrderV1SplitOrderRequest,
    type PayoutTraderOrderV1SplitOrderResponse,
    type PayoutTraderOrderV1VerifyReceiptRequest,
    type PayoutTraderOrderV1VerifyReceiptResponse,
    type TraderPayoutOrderServiceListOrdersParams,
} from '~/generated/payoutBackend/model';
import {
    traderPayoutOrderServiceGetOrder,
    getTraderPayoutOrderServiceListOrdersUrl,
    traderPayoutOrderServiceAcceptOrder,
    traderPayoutOrderServiceDeclineOrder,
    traderPayoutOrderServiceListOrders,
    traderPayoutOrderServiceSplitOrder,
    traderPayoutOrderServiceVerifyReceipt,
} from '~/generated/payoutBackend/order';
import { $authUserId } from '../../../auth';
import { type Currency } from '../../../currency';
import { type Status, type PublicStatus, type Priority } from '../model';

export const useOrder = (id?: string) => {
    const userId = useUnit($authUserId);

    return useQuery({
        enabled: Boolean(id && userId),
        refetchOnWindowFocus: true,
        retry: false,
        queryFn: () =>
            traderPayoutOrderServiceGetOrder(
                {
                    'orderId.value': id,
                },
                {
                    context: {
                        skipErrorNotification: true,
                    },
                },
            ),
        queryKey: [queryKeys.PAYOUT_ORDER, userId, id],
    });
};

type QueryOptions<Data> = Omit<UseQueryOptions<Data, Error, Data>, 'queryFn' | 'queryKey'>;

interface OrdersParams extends Omit<
    TraderPayoutOrderServiceListOrdersParams,
    'filter.statuses' | 'filter.publicStatuses' | 'filter.currency' | 'filter.priority'
> {
    'filter.statuses'?: Status[];
    'filter.publicStatuses'?: PublicStatus[];
    'filter.currency'?: Currency[];
    'filter.priority'?: Priority[];
}

type OrdersListResponse = Awaited<ReturnType<typeof traderPayoutOrderServiceListOrders>>;
type OrdersListQueryOptions = QueryOptions<OrdersListResponse>;

export const useOrdersList = (params: OrdersParams, options: OrdersListQueryOptions = {}) => {
    return useQuery({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => traderPayoutOrderServiceListOrders(params as TraderPayoutOrderServiceListOrdersParams),
        queryKey: [
            queryKeys.PAYOUT_ORDERS,
            getTraderPayoutOrderServiceListOrdersUrl(params as TraderPayoutOrderServiceListOrdersParams),
        ],
        ...options,
    });
};

interface MutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

const staleMutationRequestOptions: RequestInit = {
    context: {
        skipErrorNotificationStatuses: [409],
    },
};

const isHttpStatusError = (error: unknown, status: number) => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return false;
    }

    return (error as { response?: { status?: unknown } }).response?.status === status;
};

const invalidateOrderDetail = async (queryClient: QueryClient, userId: string | null, orderId?: string) => {
    if (!userId || !orderId) {
        return;
    }

    await queryClient.invalidateQueries({
        queryKey: [queryKeys.PAYOUT_ORDER, userId, orderId],
    });
};

const invalidateOrderQueries = async (queryClient: QueryClient, userId: string | null, orderId?: string) => {
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: [queryKeys.PAYOUT_ORDERS],
        }),
        invalidateOrderDetail(queryClient, userId, orderId),
    ]);
};

const showStaleOrderNotification = () => {
    notifications.show({
        color: 'yellow',
        title: 'Ордер обновился',
        message: 'Состояние ордера изменилось. Данные обновлены.',
    });
};

const handleMutationError = async (
    error: Error,
    orderId: string | undefined,
    queryClient: QueryClient,
    userId: string | null,
    onError: MutationProps['onError'],
) => {
    if (isHttpStatusError(error, 409)) {
        await invalidateOrderDetail(queryClient, userId, orderId);
        showStaleOrderNotification();
    }

    onError?.(error);
};

export const useAcceptOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayoutTraderOrderV1AcceptOrderRequest) => {
            return await traderPayoutOrderServiceAcceptOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (_response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Уведомление',
                message: 'Ордер взят в работу',
            });
        },
    });
};

export const useDeclineOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayoutTraderOrderV1DeclineOrderRequest) => {
            return await traderPayoutOrderServiceDeclineOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (_response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Уведомление',
                message: 'Ордер отклонен',
            });
        },
    });
};

interface SplitOrderMutationProps extends Omit<MutationProps, 'onSuccess'> {
    onSuccess?: (response: PayoutTraderOrderV1SplitOrderResponse) => void;
}

export const useSplitOrder = (options?: SplitOrderMutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayoutTraderOrderV1SplitOrderRequest) => {
            return await traderPayoutOrderServiceSplitOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.(response);
            notifications.show({
                color: 'green',
                title: 'Уведомление',
                message: 'Ордер разделен',
            });
        },
    });
};

export interface VerifyReceiptResult extends PayoutTraderOrderV1VerifyReceiptResponse {
    traceId: string | null;
}

export const useVerifyReceipt = () => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayoutTraderOrderV1VerifyReceiptRequest) => {
            let traceId: string | null = null;

            const response = await traderPayoutOrderServiceVerifyReceipt(payload, {
                context: {
                    skipErrorNotificationStatuses: [409],
                },
                onResponse: (rawResponse) => {
                    traceId = rawResponse.headers.get(xHeaders['trace-id']);
                },
            });

            const suitable = response?.suitable;

            if (typeof suitable !== 'boolean') {
                throw new Error('Некорректный ответ проверки чека');
            }

            return {
                ...response,
                suitable,
                traceId,
            };
        },
        onError: async (error, payload) => {
            if (isHttpStatusError(error, 409)) {
                await invalidateOrderDetail(queryClient, userId, payload.orderId?.value);
                showStaleOrderNotification();

                return;
            }

            if (isHTTPError(error)) {
                return;
            }

            notifications.show({
                color: 'red',
                title: 'Ошибка',
                message: 'Не удалось проверить чек. Попробуйте ещё раз',
            });
        },
        onSuccess: async (result, payload) => {
            if (!result.suitable) {
                return;
            }

            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);

            notifications.show({
                color: 'green',
                title: 'Уведомление',
                message: 'Ордер успешно финализирован',
            });
        },
    });
};
